import { Button, Icon } from '@design-system';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import React, { FC, FormEvent, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { Image } from '../../../Image';

export type NftTokenOption = {
  disabled: boolean;
  id: string;
  imageUris: {
    nft: string;
    print: string | false;
  };
};

export type FormValues = {
  option: string[];
};

export interface ImagePickerProps {
  className?: string;
  collectionTitle?: string;
  collectionUrl?: string;
  defaultValues: {
    option: string[];
  };
  loading: boolean;
  options: NftTokenOption[];
  onAdd: (tokenId: string[]) => void;
  onRemove: (tokenId: string) => void;
  onSubmit: (data: FormValues) => void;
  printMatchesNft?: boolean;
}

export const ImagePicker: FC<ImagePickerProps> = ({
  className,
  collectionTitle,
  collectionUrl,
  defaultValues,
  loading,
  onAdd,
  onRemove,
  onSubmit,
  options,
  printMatchesNft,
}) => {
  const { t } = useTranslation('nftPrintService');
  const form = useForm<FormValues>({ defaultValues });
  const selected = defaultValues.option;

  const handleSelectAll = useCallback(() => {
    onAdd(options.filter((o) => !o.disabled).map((o) => o.id));
  }, [onAdd, options]);

  const handleSubmit = useCallback(
    (ev: FormEvent<HTMLFormElement>) => {
      if (onSubmit) form.handleSubmit(onSubmit)(ev);
    },
    [form, onSubmit],
  );

  const isSelected = (optionId: string | number) => {
    if (selected.length === 0) {
      return false;
    }
    return selected.includes(String(optionId));
  };

  const handleChange = (optionId: string, state: string[]) => {
    if (state.includes(optionId)) {
      onRemove(optionId);
    } else {
      onAdd([optionId]);
    }
  };

  if (options.length === 0) {
    return !collectionTitle || !collectionUrl ? null : (
      <div className="w-full text-center text-neutral-4">
        {t('collectionNotFound', { title: collectionTitle })}
        <a
          className="hyperlink ml-1"
          href={collectionUrl}
          rel="noreferrer"
          target="_blank"
        >
          {t('openSea')}
        </a>
        .
      </div>
    );
  }

  return (
    <form
      className={classNames('flex flex-col gap-4', className)}
      onSubmit={handleSubmit}
    >
      <div className="flex items-center justify-between">
        <div className="font-bold">
          {t('nftCount', { count: options.length })}
        </div>
        {options.length <= 10 && (
          <button className="hyperlink" onClick={handleSelectAll} type="button">
            {t('selectAll')}
          </button>
        )}
      </div>
      <div className="grid max-h-72 grid-cols-5 gap-3 overflow-y-auto">
        {options.map((option, index) => (
          <label
            key={option.id}
            className={classNames('group relative w-full rounded-sm', {
              'cursor-pointer': !option.disabled,
              'cursor-not-allowed': option.disabled,
            })}
            htmlFor={`option-${option.id}`}
          >
            <input
              {...form.register('option', {
                onChange: (ev: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange(ev.target.value, selected),
              })}
              className="invisible absolute"
              disabled={option.disabled}
              id={`option-${option.id}`}
              name="option"
              type="checkbox"
              value={option.id}
            />
            <div
              className={classNames('relative w-full overflow-hidden', {
                'rounded-sm': !isSelected(option.id),
                'rounded-md border-2 border-t-neutral-6/80':
                  !printMatchesNft && isSelected(option.id),
                'rounded-md': isSelected(option.id),
              })}
            >
              <Image
                alt={`${option.id}`}
                eagerLoading={index <= 15}
                hoverEffect
                src={
                  isSelected(option.id) && !printMatchesNft
                    ? option.imageUris.print || ''
                    : option.imageUris.nft
                }
                unoptimized
                width={96}
              />
            </div>

            {option.disabled && (
              <div
                className={classNames(
                  'absolute inset-0 rounded-sm bg-white/60',
                  'flex flex-col items-center justify-center',
                  'text-center',
                )}
              >
                {t('alreadyPrinted')}
              </div>
            )}
            {printMatchesNft && isSelected(option.id) && (
              <div
                className={classNames(
                  'absolute animate-fadeIn rounded-sm bg-neutral-6/70',
                  'flex flex-col items-center justify-center',
                  'bottom-0.5 left-0.5 right-0.5 top-0.5 border-2 border-white',
                  'outline outline-2 outline-black',
                )}
              >
                <div className="h-6 w-6 rounded-full bg-white">
                  <Icon name="ic_check" />
                </div>
              </div>
            )}
          </label>
        ))}
      </div>
      <Button
        disabled={loading || selected.length === 0}
        fullWidth
        label={
          selected.length > 0
            ? t('reviewOrderCount', { count: selected.length })
            : t('reviewOrder')
        }
        type="submit"
        variant="primary"
      />
    </form>
  );
};
